import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { vbs } from "../lib/theme";

export function VbsGridTile({ product, priceLabel, onOpen }) {
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        width: "100%",
        textAlign: "left",
        display: "block",
        bgcolor: vbs.surface,
        borderRadius: "4px",
        overflow: "hidden",
        border: `1px solid ${vbs.hairline}`,
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          boxShadow: "0 12px 28px rgba(15,15,18,0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box sx={{ aspectRatio: "1 / 1", bgcolor: vbs.inkSoft, overflow: "hidden" }}>
        {product.imageUrl ? (
          <Box
            component="img"
            src={product.imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
            <Icon sx={{ color: "rgba(255,255,255,0.25)", fontSize: 32 }}>image</Icon>
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontFamily: vbs.fontMono,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            color: vbs.accent,
            textTransform: "uppercase",
          }}
        >
          {product.sku}
        </Typography>
        <Typography
          sx={{
            fontSize: 14.5,
            fontWeight: 650,
            lineHeight: 1.25,
            mt: 0.35,
            color: vbs.ink,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "2.5em",
          }}
        >
          {product.name}
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 750, mt: 0.75, color: vbs.ink }}>
          {priceLabel}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

VbsGridTile.propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  priceLabel: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export function VbsFeatureRow({ product, priceLabel, onOpen, divider = true }) {
  return (
    <>
      <ButtonBase
        onClick={onOpen}
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "88px 1fr auto",
          gap: 1.25,
          alignItems: "center",
          py: 1.25,
          textAlign: "left",
        }}
      >
        <Box sx={{ width: 88, height: 88, bgcolor: vbs.inkSoft, overflow: "hidden", borderRadius: "3px", flexShrink: 0 }}>
          {product.imageUrl ? (
            <Box
              component="img"
              src={product.imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
              <Icon sx={{ color: "rgba(255,255,255,0.25)" }}>image</Icon>
            </Box>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: vbs.fontMono,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: vbs.accent,
              textTransform: "uppercase",
            }}
          >
            {product.sku}
          </Typography>
          <Typography sx={{ fontSize: 16.5, fontWeight: 650, lineHeight: 1.25, mt: 0.2, color: vbs.ink }}>
            {product.name}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 15.5, fontWeight: 750, whiteSpace: "nowrap", color: vbs.ink }}>
          {priceLabel}
        </Typography>
      </ButtonBase>
      {divider ? <Box sx={{ height: "1px", bgcolor: vbs.hairline }} /> : null}
    </>
  );
}

VbsFeatureRow.propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  priceLabel: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  divider: PropTypes.bool,
};

VbsFeatureRow.defaultProps = {
  divider: true,
};
